// TOM-673: the alarm watch shows configured alarms in responsive Marina system
// cards, with each source and threshold stated once, even when its alarm is off.
// The shared value-line vocabulary, runtime honesty notes and delivery receipts
// remain authoritative. Editors expand across their group card; the page URL
// still owns the single open editor. The add picker, equipment waits,
// notifications and pause controls keep their existing behavior.

import { describeRuntimeDisabledReason, type VesselType } from '@neomaris/protocol';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { AlarmsPausedBanner } from '../AlarmsPausedBanner';
import { NewAlarmsNote } from '../NewAlarmsNote';
import { EnabledToggle } from '../EnabledToggle';
import { NotificationPrefsCard } from '../NotificationPrefsCard';
import { AlarmGroupLayout } from './AlarmGroupLayout';
import { InlineAlarmEditor } from './InlineAlarmEditor';
import { alarmDataSource } from '../../../lib/alarmDataSource';
import {
  CONDITION_UNBOUND_CHIP,
  alarmPausedNote,
  alarmSearchEmptyText,
  configuredBandLayout,
  isCustomDefinition,
  readWireTrigger,
  resolvedPathFor,
  setpointPhrase,
  triggerInVocabulary,
  waitingReasonText,
  type AlarmDefView,
  type AlarmSystemGroup,
  type ConfiguredAlarmView,
} from '../../../lib/alarmDisplay';
import { readHeroLine } from '../../../lib/activeScreen';
import { alarmRowSummary } from '../../../lib/alarmRowSummary';
import { inlineEditorVariant } from '../../../lib/alarmInlineEditor';
import { alarmSpecies } from '../../../lib/alarmRedesign';
import { allRowStatus, binaryNowLine, miniBarForRow } from '../../../lib/alarmsAllScreen';
import { liveValueLine, rowUnits, type LiveObservation } from '../../../lib/alarmLive';
import { signalLabel, type Signal } from '../../../lib/signalCatalog';
import type { AlarmEvidence } from '../../../lib/useAlarmEvidence';
import { useUnits } from '../../../lib/useUnits';
import {
  CamerasTabIcon,
  ChevronDownIcon,
  DevicesTabIcon,
  WarningCircleFillIcon,
} from '../../app/icons';
import { InstrumentGlyph, type GlyphName } from '../../dashboard/overview/InstrumentGlyph';

export interface AllAlarmsScreenProps {
  boatId: string;
  /** Every non-retired definition, severity-then-title. */
  views: readonly ConfiguredAlarmView[];
  /** Definition by id — species, trigger, resolved path, custom block. */
  definitions: ReadonlyMap<string, AlarmDefView>;
  /** The signal catalog by path — store units and channel labels. */
  catalogByPath: ReadonlyMap<string, Signal>;
  /** Which system each definition files under (derived once by the page). */
  groups: ReadonlyMap<string, AlarmSystemGroup>;
  /** Ids the ACTIVE screen is showing — the ACTIVE pill and the group signal. */
  surfacedIds: ReadonlySet<string>;
  /** A surfaced row's last transition stamp, by id — the binary trip age. */
  trippedAtById: ReadonlyMap<string, string>;
  /** "Now 12.6 V · alarms below 11.8 V" per id; absent ⇒ no claimable reading. */
  valueLines: ReadonlyMap<string, string>;
  /**
   * A path's current observation — the page's own
   * `observationLookup({ live, catalog })`, so the inline editor's live proofs
   * answer from exactly the reading the row's value line was composed from.
   */
  observationFor: (path: string) => LiveObservation;
  /** 6 h series by path — the mini-bar's only honest source of bounds. */
  evidence: AlarmEvidence;
  /** Search text, owned by the PAGE so it survives a band refetch. */
  query: string;
  /** Live-only boat mode; `null` until a `mode_change` frame arrives. */
  currentMode: string | null;
  /** Mirrored vessel type — it changes the row WORDS, never the data. */
  vesselType: VesselType | null;
  /** Presence; gates the pause controls and the toggle's offline wording. */
  boatOnline: boolean;
  /** The pause confirm's count. `null` is UNKNOWN and must not read as zero. */
  activeCount: number | null;
  /** The boat's paused mirror, for the foot-of-page pause affordance. */
  alarmsPausedAt: string | null;
  /** `isAlarmsPaused(alarmsPausedAt)` — the whisper hides while paused. */
  alarmsPaused: boolean;
  /** `describeBoatSync(...)` — the inline editor's footer caption reads it. */
  boatSyncNote: string | null;
  /**
   * The ONE row whose inline editor is open, from the page's `?editing=`.
   * `null` closes every panel — only one may be open page-wide.
   */
  editingId: string | null;
  /** Open (id) or close (`null`) the inline editor, through the URL. */
  onEditingChange: (alarmId: string | null) => void;
  /** Re-read the snapshot after a toggle / create / delete. */
  onChange: () => void;
  /** Re-read the snapshot so a relayed pause echo lands without a reload. */
  onPauseSent: () => void;
  /** The page clock (30 s tick), shared so freshness wording never disagrees. */
  now: number;
}

/**
 * "Not evaluating — …", with the reason put through the SHARED describer
 * (`@neomaris/protocol`) rather than printed verbatim. `paused_by_owner` and
 * friends are machine tokens; one token, one wording, wherever it is read.
 */
function notEvaluatingLabel(reason: string | undefined): string {
  const described = describeRuntimeDisabledReason(reason);
  return described !== undefined ? `Not evaluating — ${described}` : 'Not evaluating';
}

/** Group heading id-safe slug — `Safety` → `safety`. */
function groupSlug(group: AlarmSystemGroup): string {
  return group.toLowerCase();
}

const GROUP_ICONS: Readonly<
  Record<Exclude<AlarmSystemGroup, 'Safety' | 'Cameras' | 'Other'>, GlyphName>
> = {
  Engine: 'engine',
  Power: 'bolt',
  Tanks: 'water',
  Navigation: 'helm',
  Comfort: 'thermometer',
};

function AlarmSystemIcon({ group }: { group: AlarmSystemGroup }) {
  if (group === 'Safety') return <WarningCircleFillIcon />;
  if (group === 'Cameras') return <CamerasTabIcon />;
  if (group === 'Other') return <DevicesTabIcon />;
  return <InstrumentGlyph name={GROUP_ICONS[group]} />;
}

function AllAlarmRow({
  view,
  boatId,
  def,
  catalogByPath,
  valueLine,
  evidence,
  surfaced,
  trippedAt,
  currentMode,
  vesselType,
  observationFor,
  boatOnline,
  boatSyncNote,
  editing,
  onEditingChange,
  savedNote,
  now,
  onChange,
  onSaved,
}: {
  view: ConfiguredAlarmView;
  boatId: string;
  def: AlarmDefView | undefined;
  catalogByPath: ReadonlyMap<string, Signal>;
  valueLine: string | undefined;
  evidence: AlarmEvidence;
  surfaced: boolean;
  /** A surfaced row's last transition stamp — the binary NOW cell's trip age. */
  trippedAt: string | null;
  currentMode: string | null;
  vesselType: VesselType | null;
  observationFor: (path: string) => LiveObservation;
  boatOnline: boolean;
  boatSyncNote: string | null;
  /** This row's inline editor is the open one. */
  editing: boolean;
  onEditingChange: (alarmId: string | null) => void;
  /**
   * What the boat still has to do with THIS row's last inline save, or `null`
   * when there is nothing to say — the boat already has it, or the last save
   * was some other row's.
   */
  savedNote: string | null;
  now: number;
  onChange: () => void;
  onSaved: (deliveryNote: string | null) => void;
}) {
  const units = useUnits();
  const status = allRowStatus({
    enabled: view.enabled,
    surfaced,
    compilePending: view.compilePending,
    runtimeDisabledReason: view.runtimeDisabledReason,
    severity: view.severity,
  });
  const dataSource = useMemo(
    () => (def === undefined ? null : alarmDataSource(def, new Set(catalogByPath.keys()))),
    [def, catalogByPath]
  );
  const path = dataSource === null ? null : resolvedPathFor(dataSource);
  const signal = path === null ? undefined : catalogByPath.get(path);
  const trigger = dataSource === null ? null : readWireTrigger(dataSource);
  const species = dataSource === null ? 'none' : alarmSpecies(dataSource);
  const bar = miniBarForRow({
    species,
    series: path === null ? undefined : evidence.series.get(path),
    threshold: trigger?.setpoint ?? null,
  });
  const sensorLine =
    !view.enabled && dataSource !== null
      ? (liveValueLine(dataSource, {
          observationFor,
          unitFor: (sourcePath) => catalogByPath.get(sourcePath)?.unit,
          units,
          now,
        }) ?? undefined)
      : valueLine;
  // An off binary alarm reports its sensor value, never an OK evaluation.
  const nowLine =
    species === 'binary' && view.enabled
      ? binaryNowLine({ surfaced, trippedAt, now, hasReading: valueLine !== undefined })
      : sensorLine;
  const pausedNote = alarmPausedNote(view, currentMode, vesselType);

  // What the row watches, in the installer's words where there are any: a
  // channel label beats a path, and a path beats nothing. Never invented — a
  // definition with no resolved path says nothing here.
  const signalText =
    path === null
      ? undefined
      : (signal?.displayLabel ??
        signalLabel(path, {
          entityName: signal?.entityName,
          deviceName: signal?.deviceName,
          unit: signal?.unit,
          channelLabel: signal?.channelLabel,
        }));

  const row =
    def === undefined
      ? null
      : rowUnits(signal?.unit, path ?? '', units, {
          rescale: !isCustomDefinition(def),
        });
  const triggerPhrase =
    row !== null
      ? setpointPhrase(triggerInVocabulary(trigger, row.vocab), row.vocab.unit, row.category, units)
      : null;
  // The composed line's last middot separates the trigger from the reading.
  // Keep the value and age verbatim, even while a catalog refresh changes the
  // unit vocabulary of the separate rule summary.
  const triggerAt = triggerPhrase === null ? -1 : (nowLine?.lastIndexOf(' · ') ?? -1);
  const readingLine =
    nowLine !== undefined && triggerAt !== -1 ? nowLine.slice(0, triggerAt) : nowLine;
  const reading = readingLine === undefined ? null : readHeroLine(readingLine);
  const summary = alarmRowSummary({
    def,
    view,
    sourceLabel: signalText,
    triggerPhrase,
    triggerShown: false,
    unit: signal?.unit,
    units,
    labelFor: (sourcePath) => {
      const source = catalogByPath.get(sourcePath);
      return (
        source?.displayLabel ??
        signalLabel(sourcePath, {
          entityName: source?.entityName,
          deviceName: source?.deviceName,
          unit: source?.unit,
          channelLabel: source?.channelLabel,
        })
      );
    },
    unitFor: (sourcePath) => catalogByPath.get(sourcePath)?.unit,
  });

  const panelId = `alarm-inline-panel-${view.id}`;
  // The ACTIVE screen's EDIT deep-links into this panel, and the row it belongs
  // to is often far below the fold. Bring it into view when it opens — a panel
  // that opened somewhere off screen reads as a button that did nothing.
  const panelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!editing) return;
    panelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [editing]);

  return (
    <>
      <li
        className={
          'al-row' +
          (status.tint === null ? '' : ` al-row--${status.tint}`) +
          (view.enabled ? '' : ' al-row--off alarm-row--muted') +
          (editing ? ' al-row--expanded' : '')
        }
        data-testid={`alarm-config-${view.id}`}
      >
        <div className="al-row__main">
          <div className="al-row__heading">
            <div className="al-row__identity">
              <Link
                className="alarm-row__title al-row__name"
                to={`/dashboard/boats/${boatId}/alarms/${view.id}`}
                data-testid={`alarm-config-link-${view.id}`}
              >
                {summary.title}
              </Link>
              {status.kind !== 'off' && status.kind !== 'quiet' ? (
                <span className={`al-pill al-pill--${status.kind}`}>{status.label}</span>
              ) : null}
            </div>
            <div className="al-row__now">
              {bar !== null ? (
                <svg
                  className="al-minibar"
                  viewBox="0 0 100 12"
                  width="100"
                  height="12"
                  aria-hidden="true"
                >
                  <rect className="al-minibar__track" x="0" y="4" width="100" height="4" rx="2" />
                  <rect
                    className={`al-minibar__fill${view.enabled && surfaced ? ' al-minibar__fill--active' : ''}`}
                    x="0"
                    y="4"
                    width={bar.pct}
                    height="4"
                    rx="2"
                  />
                  {bar.tickPct !== null ? (
                    <line
                      className="al-minibar__tick"
                      x1={bar.tickPct}
                      x2={bar.tickPct}
                      y1="0"
                      y2="12"
                    />
                  ) : null}
                </svg>
              ) : null}
              {/* A single current reading, with its original unit and reported age. */}
              {reading !== null ? (
                <span
                  className="alarm-now al-row__value"
                  data-testid={`alarm-config-value-${view.id}`}
                >
                  {reading.kind === 'now' ? (
                    <>
                      <span className="al-row__reading">
                        <span className="al-row__lead">{reading.lead}</span>
                        <span className="al-row__number">{reading.value}</span>
                        <span className="al-row__unit">{reading.unit}</span>
                      </span>
                    </>
                  ) : (
                    reading.text
                  )}
                </span>
              ) : (
                <span className="al-row__value al-row__value--none">—</span>
              )}
            </div>
          </div>
          {summary.source !== null ? (
            <p className="al-row__sub" data-testid={`alarm-config-watching-${view.id}`}>
              {summary.source}
            </p>
          ) : null}
          {/* The honesty chips. Each says something the pills cannot: WHY the
            boat is not evaluating this row, or that an authored condition is
            not actually gating it (D2). */}
          {view.compilePending || view.runtimeDisabledReason || view.conditionUnbound ? (
            <p className="al-row__chips">
              {view.compilePending ? (
                <span
                  className="alarm-chip alarm-chip--attention"
                  data-testid={`alarm-pending-${view.id}`}
                >
                  {notEvaluatingLabel(view.compileReason)}
                </span>
              ) : null}
              {view.runtimeDisabledReason ? (
                <span
                  className="alarm-chip alarm-chip--attention"
                  data-testid={`alarm-runtime-disabled-${view.id}`}
                >
                  {notEvaluatingLabel(view.runtimeDisabledReason)}
                </span>
              ) : null}
              {view.conditionUnbound ? (
                <span
                  className="alarm-chip alarm-chip--attention"
                  data-testid={`alarm-condition-unbound-${view.id}`}
                >
                  {CONDITION_UNBOUND_CHIP}
                </span>
              ) : null}
            </p>
          ) : null}
          {/* Enabled, armed, and the boat is in a mode this alarm does not watch.
            Directly under the row it is about. */}
          {pausedNote !== undefined ? (
            <p className="alarm-paused al-row__sub" data-testid={`alarm-config-paused-${view.id}`}>
              {pausedNote}
            </p>
          ) : null}
          {/* The last inline save on THIS row, and only when the boat still has
              something to do about it (`describeDelivery` says nothing once the
              change has landed). It stays beside its own alarm rather than
              becoming a page banner — the row is where the owner is looking,
              and the edited numbers above are the proof that the cloud has it.
              Never a cheerful "Saved!": the claim worth making is what the boat
              is running. */}
          {savedNote !== null ? (
            <p
              className="al-row__sub al-row__saved"
              role="status"
              data-testid={`alarm-inline-saved-${view.id}`}
            >
              {savedNote}
            </p>
          ) : null}
        </div>

        <div className="al-row__trigger">
          {summary.details.length > 0 ? (
            <p className="alarm-modes" data-testid={`alarm-config-summary-${view.id}`}>
              {summary.details.join(' · ')}
            </p>
          ) : null}
        </div>

        <div className="al-row__actions" role="group" aria-label={`Controls for ${view.title}`}>
          {/* The common edit expands the row in place; the caret flips
            while the panel is open. A definition the page never received has
            nothing to expand INTO, so it keeps the link to the full editor
            rather than offering a toggle that would open an empty panel. */}
          {def === undefined ? (
            <Link
              className="al-textbtn al-row__edit"
              aria-label={`Edit ${view.title}`}
              to={`/dashboard/boats/${boatId}/alarms/${view.id}`}
            >
              Edit
            </Link>
          ) : (
            <button
              type="button"
              className="al-textbtn al-row__edit"
              aria-label={`Edit ${view.title}`}
              aria-expanded={editing}
              aria-controls={panelId}
              data-testid={`alarm-config-edit-${view.id}`}
              onClick={() => onEditingChange(editing ? null : view.id)}
            >
              Edit
              <ChevronDownIcon
                className={`al-row__caret${editing ? ' al-row__caret--open' : ''}`}
              />
            </button>
          )}

          <EnabledToggle
            boatId={boatId}
            view={view}
            withdrawn={def !== undefined && inlineEditorVariant(def) === 'withdrawn'}
            onChange={onChange}
          />
        </div>
      </li>

      {/* A sibling keeps the editor across the whole group card. */}
      {editing && def !== undefined ? (
        <li className="al-editor-host" id={panelId} ref={panelRef}>
          <InlineAlarmEditor
            boatId={boatId}
            view={view}
            def={def}
            catalogByPath={catalogByPath}
            observationFor={observationFor}
            vesselType={vesselType}
            boatOnline={boatOnline}
            boatSyncNote={boatSyncNote}
            now={now}
            onClose={() => onEditingChange(null)}
            onSaved={onSaved}
          />
        </li>
      ) : null}
    </>
  );
}

export function AllAlarmsScreen({
  boatId,
  views,
  definitions,
  catalogByPath,
  groups,
  surfacedIds,
  valueLines,
  trippedAtById,
  observationFor,
  evidence,
  query,
  currentMode,
  vesselType,
  boatOnline,
  activeCount,
  alarmsPausedAt,
  alarmsPaused,
  boatSyncNote,
  editingId,
  onEditingChange,
  onChange,
  onPauseSent,
  now,
}: AllAlarmsScreenProps) {
  // What the BOAT still has to do with the change that just saved
  // (`describeDelivery`), and which row it was about. The panel that produced it
  // is closing, so the sentence lives out here — but it stays attached to its
  // own row rather than moving to the top of the screen, where a save made on a
  // row far down the list would put it out of sight. Cleared the moment another
  // editor opens: it is about the last save, not about the row now on screen.
  const [saved, setSaved] = useState<{ id: string; note: string } | null>(null);
  useEffect(() => {
    if (editingId !== null) setSaved(null);
  }, [editingId]);

  // Filing, searching and the waiting fold are all decided by the shared pure
  // layout — the same search semantics (title + group substring) the band it
  // replaces used, so a saved query keeps matching what it always matched.
  const layout = configuredBandLayout(views, {
    groupFor: (view) => groups.get(view.id) ?? 'Other',
    surfacedIds,
    query,
  });
  const nothingMatches = layout.shown === 0;

  // Secondary to adding one alarm, and worded as prose rather than a button:
  // most visits add a single thing, while asking the boat to work out a whole
  // set is the rarer, bigger move — after new gear, or when a boat has lost its
  // definitions.
  const suggestAlarmsLink = (
    <Link
      className="al-textbtn"
      to={`/dashboard/boats/${boatId}/alarms/regenerate`}
      data-testid="alarms-regenerate-btn"
    >
      Suggest alarms
    </Link>
  );

  return (
    <section className="al-screen al-all" data-testid="alarms-configured-band">
      <NewAlarmsNote boatId={boatId} />
      {views.length === 0 ? (
        <div className="empty-card" data-testid="alarms-configured-empty">
          <h3>No alarms configured yet.</h3>
          <p className="muted">
            Use + to choose a source for your first alarm, or ask Neo to help you create one.
          </p>
        </div>
      ) : null}

      {views.length === 0 ? null : (
        <>
          {nothingMatches ? (
            <p className="al-note" data-testid="alarms-search-empty">
              {alarmSearchEmptyText(query)}
            </p>
          ) : null}
          <AlarmGroupLayout>
            {layout.sections.map((section) => (
              <div
                className="alarm-group al-group al-card"
                key={section.group}
                data-system={groupSlug(section.group)}
                data-testid={`alarms-group-${groupSlug(section.group)}`}
              >
                <h3 className="al-group-heading">
                  <span className="al-group__icon" aria-hidden="true">
                    <AlarmSystemIcon group={section.group} />
                  </span>
                  <span className="alarm-group__name">{section.group}</span>
                  <span className="alarm-group__count">
                    <span
                      className={`alarm-group__dot alarm-group__dot--${section.tone}`}
                      aria-hidden="true"
                    />
                    {section.rows.length}
                  </span>
                </h3>
                <ul className="al-rows">
                  {section.rows.map((view) => (
                    <AllAlarmRow
                      key={view.id}
                      view={view}
                      boatId={boatId}
                      def={definitions.get(view.id)}
                      catalogByPath={catalogByPath}
                      valueLine={valueLines.get(view.id)}
                      evidence={evidence}
                      surfaced={surfacedIds.has(view.id)}
                      trippedAt={trippedAtById.get(view.id) ?? null}
                      currentMode={currentMode}
                      vesselType={vesselType}
                      observationFor={observationFor}
                      boatOnline={boatOnline}
                      boatSyncNote={boatSyncNote}
                      editing={editingId === view.id}
                      onEditingChange={onEditingChange}
                      savedNote={saved?.id === view.id ? saved.note : null}
                      now={now}
                      onChange={onChange}
                      onSaved={(deliveryNote) => {
                        setSaved(
                          deliveryNote === null ? null : { id: view.id, note: deliveryNote }
                        );
                        onChange();
                      }}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </AlarmGroupLayout>
        </>
      )}

      <div className="al-footer">
        <div className="al-links">
          {/* First in the row, and OUTSIDE the waiting fold's condition — a
                  boat with no waiting gear must not lose the only link to the
                  regenerate flow. */}
          {suggestAlarmsLink}

          {/* Never evaluated, because the gear has never reported. Not a fault
                to chase and not a row to price like the others — a wait. Folded
                away, with the count visible so it is never hidden. */}
          {layout.waiting.length > 0 ? (
            <details className="al-fold" data-testid="alarms-waiting-equipment">
              <summary>Waiting for equipment ({layout.waiting.length})</summary>
              <ul className="al-card al-rows al-rows--waiting">
                {layout.waiting.map((view) => (
                  <li
                    className="al-row al-row--off alarm-row--muted"
                    key={view.id}
                    data-testid={`alarm-config-${view.id}`}
                  >
                    <div className="al-row__main">
                      <Link
                        className="alarm-row__title al-row__name"
                        to={`/dashboard/boats/${boatId}/alarms/${view.id}`}
                        data-testid={`alarm-config-link-${view.id}`}
                      >
                        {view.title}
                      </Link>
                      {/* No toggle down here: a definition with nothing to
                            watch is not a switch worth offering. */}
                      <p className="al-row__sub" data-testid={`alarm-waiting-reason-${view.id}`}>
                        {waitingReasonText(view.compileReason)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {/* Reduced to a link by the design; the card behind it is unchanged,
                down to its rate-limit and dry-run disclosures. */}
          <details className="al-fold">
            <summary>Notifications</summary>
            <NotificationPrefsCard boatId={boatId} />
          </details>
        </div>

        {/* The quiet end of the pause control: pausing every alarm aboard is
                a rare, deliberate act, so it gets a whisper at the foot and a
                confirm — never a button beside the rows. While the boat IS
                paused the loud banner runs at the top of the page instead.

                It sits BELOW the links rather than inside them (the design's
                right-aligned link): the confirm this opens is a panel of
                sentences, and squeezed into the tail of a flex row it would be
                unreadable exactly when it is being read most carefully. */}
        {alarmsPaused ? null : (
          <div className="al-footer__pause">
            <AlarmsPausedBanner
              boatId={boatId}
              pausedAt={alarmsPausedAt}
              boatOnline={boatOnline}
              activeCount={activeCount}
              onSent={onPauseSent}
            />
          </div>
        )}
      </div>
    </section>
  );
}
